// pages/hauler/ProfileScreen.js
// Hauler profile — read-only summary + informational reliability score
// + inline editors for crew tier and VIN.
//
// Edit path: supabase.rpc("upsert_driver_profile", { payload }) — the
// same RPC used by the retired shell. No new write paths invented; payload
// shape is { vin } or { crew_tier }. Successful patches propagate up
// to HaulerApp via onProfileUpdated.
//
// Bucket A+B contract: surface the score number and the mandated
// disclosure copy. NO band pill, NO score deltas, NO history events.
// Per IC compliance, the score is informational and never gates
// eligibility.

import React, { useEffect, useState } from "react";
import { supabase, signOut } from "../../services/supabaseService";
import { fetchLoaderPerformance } from "../../services/loaderPerformance";
import PayoutSetupCard from "../../components/hauler/PayoutSetupCard";

const COLORS = {
  text: "var(--hl-text)",
  sub: "var(--hl-text-sub)",
  muted: "var(--hl-text-muted)",
  card: "var(--hl-card)",
  border: "var(--hl-border)",
  green: "var(--hl-green)",
  sky: "var(--hl-sky)",
  red: "var(--hl-red)",
};

const CREW_OPTIONS = [
  { key: "solo", label: "Solo",       sub: "Under 50 lbs per item" },
  { key: "pair", label: "Two-person", sub: "50 to 150 lbs per item" },
  { key: "crew", label: "Crew",       sub: "Over 150 lbs per item" },
];

const BED_LABEL = {
  short: "Short bed", standard: "Standard bed", long: "Long bed", custom: "Custom bed",
};

const MARKET_LABEL = {
  "ut-hill-afb":     "Hill AFB",
  "tx-fort-cavazos": "Fort Cavazos",
};

function displayName(profile) {
  const f = (profile?.first_name || "").trim();
  const l = (profile?.last_name || "").trim();
  if (f && l) return `${f} ${l[0]}.`;
  if (f) return f;
  return "Hauler";
}

function vehicleLine(profile) {
  const make = (profile?.vehicle_make || "").trim();
  const model = (profile?.vehicle_model || "").trim();
  const bed = profile?.bed_size ? BED_LABEL[profile.bed_size] : "";
  const trailer = profile?.has_trailer ? "Trailer ready" : null;
  const parts = [];
  if (make || model) parts.push([make, model].filter(Boolean).join(" "));
  if (bed) parts.push(bed);
  if (trailer) parts.push(trailer);
  return parts.length ? parts.join(" · ") : "Vehicle pending";
}

// VIN sanitization mirrors the retired shell behavior: uppercase, strip
// non-VIN chars (I, O, Q removed by the regex), 17-char cap.
function sanitizeVin(input) {
  return (input || "").toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17);
}

export default function ProfileScreen({ session, driverProfile, onProfileUpdated, colorScheme }) {
  const [score, setScore] = useState(null);
  const [scoreErr, setScoreErr] = useState(null);

  // Edit state
  const [openPanel, setOpenPanel] = useState(null);  // 'vin' | 'tier' | null
  const [vinDraft, setVinDraft] = useState(driverProfile?.vin || "");
  const [tierDraft, setTierDraft] = useState(driverProfile?.crew_tier || "solo");
  const [saving, setSaving] = useState(null);        // 'vin' | 'tier' | null
  const [editMessage, setEditMessage] = useState(null); // { kind, text }
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const accountEmail = session?.user?.email || "";

  useEffect(() => {
    setVinDraft(driverProfile?.vin || "");
    setTierDraft(driverProfile?.crew_tier || "solo");
  }, [driverProfile?.vin, driverProfile?.crew_tier]);

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) { setScore(null); return; }
    let cancelled = false;
    fetchLoaderPerformance(userId)
      .then((perf) => { if (!cancelled) setScore(perf); })
      .catch((err) => {
        // Keep the technical detail in console; show generic copy in UI.
        console.error("[ProfileScreen] score fetch failed:", err);
        if (!cancelled) setScoreErr(true);
      });
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  // ── Save handlers ─────────────────────────────────────────
  async function saveVin() {
    const clean = sanitizeVin(vinDraft);
    setVinDraft(clean);
    if (clean.length !== 17) {
      setEditMessage({ kind: "err", text: "VIN must be 17 characters. I, O, Q are not used." });
      return;
    }
    await callUpsert({ vin: clean }, "vin", "Vehicle VIN updated");
  }

  async function saveTier() {
    if (!tierDraft) return;
    await callUpsert({ crew_tier: tierDraft }, "tier", "Crew tier updated");
  }

  async function toggleTrailer() {
    if (saving === "trailer") return;
    const next = !driverProfile?.has_trailer;
    await callUpsert({ has_trailer: next }, "trailer",
      next ? "Utility trailer enabled" : "Utility trailer removed"
    );
  }

  // Availability self-toggle. IC-safe: voluntary, no quota, no penalty,
  // no score impact. Persists via upsert_driver_profile RPC (which
  // started honoring is_available in migration 20260502030000).
  async function toggleAvailability() {
    if (saving === "availability") return;
    // Default true so a profile that's never explicitly toggled
    // matches the column's NOT NULL DEFAULT true.
    const current = driverProfile?.is_available !== false;
    const next = !current;
    await callUpsert({ is_available: next }, "availability",
      next ? "You're available — new jobs appear in your queue." : "Not available right now — jobs are hidden."
    );
  }

  // Job-alert opt-in self-toggle. IC-safe: voluntary, no quota, no penalty,
  // no score impact. Default ON (matches the column's NOT NULL DEFAULT true).
  // Turning this off stops the "new job available" texts; STOP replies are
  // handled separately by the Twilio Hauler messaging service.
  async function toggleJobAlerts() {
    if (saving === "jobAlerts") return;
    const current = driverProfile?.job_alerts_opt_in !== false;
    const next = !current;
    await callUpsert({ job_alerts_opt_in: next }, "jobAlerts",
      next ? "Job alert texts on — we'll text you when a matching job opens." : "Job alert texts off — you'll still see jobs in your queue."
    );
  }

  async function callUpsert(payload, busyKey, successText) {
    setSaving(busyKey);
    setEditMessage(null);
    try {
      const { data, error } = await supabase.rpc("upsert_driver_profile", { payload });
      if (error) throw error;
      const patch = data || payload;
      onProfileUpdated && onProfileUpdated(patch);
      setEditMessage({ kind: "ok", text: successText });
      setOpenPanel(null);
    } catch (e) {
      setEditMessage({ kind: "err", text: e?.message || "Update failed. Try again." });
    } finally {
      setSaving(null);
    }
  }

  async function deleteAccount() {
    const normalizedEmail = accountEmail.trim().toLowerCase();
    const normalizedConfirm = deleteConfirm.trim().toLowerCase();
    if (!normalizedEmail || normalizedConfirm !== normalizedEmail) {
      setEditMessage({ kind: "err", text: "Enter your account email exactly to confirm deletion." });
      return;
    }
    setDeleteBusy(true);
    setEditMessage(null);
    try {
      const { data: { session: activeSession } } = await supabase.auth.getSession();
      if (!activeSession?.access_token) {
        throw new Error("Your session expired. Sign in again before deleting your account.");
      }
      const { data, error } = await supabase.functions.invoke("delete-account", {
        body: { confirm_email: normalizedConfirm },
        headers: { Authorization: `Bearer ${activeSession.access_token}` },
      });
      if (error || data?.error) {
        throw new Error(data?.error || error?.message || "Account deletion failed");
      }
      await signOut();
    } catch (e) {
      setEditMessage({ kind: "err", text: e?.message || "Account deletion failed. Try again." });
    } finally {
      setDeleteBusy(false);
    }
  }

  const name = displayName(driverProfile);
  const vehicle = vehicleLine(driverProfile);
  const crewKey = driverProfile?.crew_tier || "solo";
  const crewLabel = CREW_OPTIONS.find(o => o.key === crewKey)?.label || "Solo";
  const crewSub = CREW_OPTIONS.find(o => o.key === crewKey)?.sub || "";
  const market = MARKET_LABEL[driverProfile?.market_id] || "Hill AFB";
  const vin = driverProfile?.vin || "";
  const vinDisplay = vin ? `…${vin.slice(-6)}` : "Add VIN";

  return (
    <div style={{ padding: "20px 16px 96px", color: COLORS.text }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, letterSpacing: 2, color: COLORS.muted, marginBottom: 4 }}>
          PROFILE
        </div>
        <div style={{ fontSize: 22, fontWeight: 600 }}>Profile</div>
      </div>

      {/* Availability card — IC-safe self-toggle. Voluntary, no
          quota, no penalty, no score impact. Default ON; the
          column's server-side default is true. */}
      <div style={cardStyle}>
        <div style={sectionLabel}>Availability</div>
        <button
          type="button"
          onClick={toggleAvailability}
          disabled={saving === "availability"}
          aria-pressed={driverProfile?.is_available !== false}
          style={{
            width: "100%",
            marginTop: 8,
            padding: "14px 12px",
            borderRadius: 12,
            border: `1px solid ${(driverProfile?.is_available !== false) ? COLORS.green : COLORS.border}`,
            background: (driverProfile?.is_available !== false) ? "rgba(3,166,106,.12)" : "var(--hl-input-bg)",
            color: COLORS.text,
            fontFamily: "inherit",
            cursor: saving === "availability" ? "wait" : "pointer",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: 12,
            minHeight: 60,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: (driverProfile?.is_available !== false) ? COLORS.green : COLORS.muted,
              boxShadow: (driverProfile?.is_available !== false) ? "0 0 8px rgba(3,166,106,.6)" : "none",
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {(driverProfile?.is_available !== false) ? "You're available" : "Not available right now"}
            </div>
            <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 2, lineHeight: 1.4 }}>
              {(driverProfile?.is_available !== false)
                ? "New jobs appear in your queue."
                : "Jobs are hidden. Toggle on when you're ready to work."}
            </div>
          </div>
          <span style={{ fontSize: 11, color: COLORS.sub, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
            {saving === "availability" ? "…" : ((driverProfile?.is_available !== false) ? "On" : "Off")}
          </span>
        </button>

        {/* Job alert texts — opt-in SMS when a matching job opens.
            Voluntary; turning it off never hides jobs or affects score. */}
        <div style={{ marginTop: 6 }}>
          <ToggleRow
            label="Job alert texts"
            sublabel="Text me when a job my truck can handle opens"
            checked={driverProfile?.job_alerts_opt_in !== false}
            loading={saving === "jobAlerts"}
            onToggle={toggleJobAlerts}
          />
        </div>
      </div>

      {/* Account card */}
      <div style={cardStyle}>
        <div style={sectionLabel}>Account</div>
        <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{name}</div>
        <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 4 }}>{vehicle}</div>
        <div style={dividerStyle} />

        {/* Vehicle VIN — editable */}
        <EditableRow
          label="Vehicle VIN"
          value={vinDisplay}
          valueColor={vin ? COLORS.text : COLORS.sky}
          isOpen={openPanel === "vin"}
          onToggle={() => { setOpenPanel(openPanel === "vin" ? null : "vin"); setEditMessage(null); }}
        >
          <input
            type="text"
            value={vinDraft}
            onChange={(e) => { setVinDraft(sanitizeVin(e.target.value)); setEditMessage(null); }}
            placeholder="17-character VIN"
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              background: "var(--hl-input-bg)",
              color: COLORS.text,
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 1,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 10 }}>
            <span style={{ fontSize: 11, color: COLORS.sub }}>{vinDraft.length}/17</span>
            <button
              type="button"
              disabled={saving === "vin"}
              onClick={saveVin}
              style={primaryBtn(saving === "vin")}
            >
              {saving === "vin" ? "Saving…" : "Save VIN"}
            </button>
          </div>
        </EditableRow>

        {/* Crew tier — editable */}
        <EditableRow
          label="Crew tier"
          value={
            <>
              <span>{crewLabel}</span>
              <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2, fontWeight: 400 }}>
                {crewSub}
              </div>
            </>
          }
          isOpen={openPanel === "tier"}
          onToggle={() => { setOpenPanel(openPanel === "tier" ? null : "tier"); setEditMessage(null); }}
        >
          <div style={{ display: "grid", gap: 8 }}>
            {CREW_OPTIONS.map(opt => {
              const active = tierDraft === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => { setTierDraft(opt.key); setEditMessage(null); }}
                  style={{
                    border: `1px solid ${active ? COLORS.sky : COLORS.border}`,
                    borderRadius: 10,
                    background: active ? "rgba(61,144,217,.16)" : "var(--hl-input-bg)",
                    color: COLORS.text,
                    padding: "11px 12px",
                    fontFamily: "inherit",
                    textAlign: "left",
                    cursor: "pointer",
                    minHeight: 44,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 800 }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: COLORS.sub, marginTop: 2 }}>{opt.sub}</div>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            disabled={saving === "tier"}
            onClick={saveTier}
            style={{ ...primaryBtn(saving === "tier"), width: "100%", marginTop: 10 }}
          >
            {saving === "tier" ? "Saving…" : "Save crew tier"}
          </button>
        </EditableRow>

        {/* Utility trailer — boolean toggle (no expanding editor) */}
        <ToggleRow
          label="Utility trailer"
          sublabel="Adds capacity for Full loads"
          checked={!!driverProfile?.has_trailer}
          loading={saving === "trailer"}
          onToggle={toggleTrailer}
        />

        {/* Read-only rows */}
        <Row label="Market" value={market} />
        {driverProfile?.has_base_access && (
          <Row label="Base access" value="Granted" />
        )}

        {/* Delete account — chevron dropdown, same EditableRow pattern as
            Vehicle VIN / Crew tier above (muted row, not a separate red
            "Danger Zone" card). Reuses the existing deleteOpen/deleteConfirm/
            deleteBusy state and deleteAccount() handler unchanged — only the
            trigger + placement changed. */}
        <EditableRow
          label="Delete account"
          value=""
          isOpen={deleteOpen}
          onToggle={() => {
            const next = !deleteOpen;
            setDeleteOpen(next);
            if (next) setDeleteConfirm("");
            setEditMessage(null);
          }}
        >
          <div style={{ fontSize: 12, color: COLORS.sub, lineHeight: 1.5, marginBottom: 10 }}>
            Permanently disable your Hauler login. This is blocked while you have active assigned work.
          </div>
          <div style={{ fontSize: 12, color: COLORS.sub, lineHeight: 1.5, marginBottom: 10 }}>
            Type <strong>{accountEmail || "your email"}</strong> to confirm.
          </div>
          <input
            type="email"
            value={deleteConfirm}
            onChange={(e) => { setDeleteConfirm(e.target.value); setEditMessage(null); }}
            placeholder={accountEmail || "email@example.com"}
            autoCapitalize="none"
            autoCorrect="off"
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              background: "var(--hl-input-bg)",
              color: COLORS.text,
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 700,
              outline: "none",
              boxSizing: "border-box",
              minHeight: 44,
            }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
            <button
              type="button"
              onClick={() => { setDeleteOpen(false); setDeleteConfirm(""); setEditMessage(null); }}
              disabled={deleteBusy}
              style={{
                minHeight: 44,
                borderRadius: 11,
                border: `1px solid ${COLORS.border}`,
                background: "transparent",
                color: COLORS.text,
                fontFamily: "inherit",
                fontWeight: 800,
                cursor: deleteBusy ? "wait" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={deleteAccount}
              disabled={deleteBusy || deleteConfirm.trim().toLowerCase() !== accountEmail.trim().toLowerCase()}
              style={{
                minHeight: 44,
                borderRadius: 11,
                border: "none",
                background: deleteBusy || deleteConfirm.trim().toLowerCase() !== accountEmail.trim().toLowerCase() ? "var(--hl-input-bg)" : COLORS.red,
                color: deleteBusy || deleteConfirm.trim().toLowerCase() !== accountEmail.trim().toLowerCase() ? COLORS.sub : "#fff",
                fontFamily: "inherit",
                fontWeight: 800,
                cursor: deleteBusy ? "wait" : deleteConfirm.trim().toLowerCase() === accountEmail.trim().toLowerCase() ? "pointer" : "not-allowed",
              }}
            >
              {deleteBusy ? "Deleting..." : "Confirm delete"}
            </button>
          </div>
        </EditableRow>

        {editMessage && (
          <div style={{
            marginTop: 10,
            padding: "8px 10px",
            borderRadius: 10,
            fontSize: 12,
            fontWeight: 700,
            background: editMessage.kind === "ok" ? "rgba(3,166,106,.14)" : "rgba(217,82,82,.14)",
            border: `1px solid ${editMessage.kind === "ok" ? COLORS.green : COLORS.red}`,
            color: editMessage.kind === "ok" ? COLORS.green : COLORS.red,
          }}>
            {editMessage.text}
          </div>
        )}
      </div>

      {/* Payouts — Stripe Connect onboarding status + hosted-setup entry point.
          Phase 1 of Hauler Payouts. Status is read live via the
          connect-onboarding edge function; nothing is cached client-side. */}
      <PayoutSetupCard session={session} />

      {/* Field guide — always-available download for approved Haulers */}
      <div style={cardStyle}>
        <div style={sectionLabel}>Field guide</div>
        <a
          href="/hauler-field-guide.pdf"
          download="LoadDash-Hauler-Field-Guide.pdf"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 10,
            textDecoration: "none",
            color: COLORS.text,
            minHeight: 44,
          }}
        >
          <span style={{ flex: 1 }}>
            <span style={{ display: "block", fontSize: 14, fontWeight: 700 }}>The Hauler field guide</span>
            <span style={{ display: "block", fontSize: 12, color: COLORS.sub, lineHeight: 1.45, marginTop: 2 }}>
              Pay math, what LoadDash takes, and the five-tap job. PDF.
            </span>
          </span>
          <span style={{ color: COLORS.sky, fontSize: 12, fontWeight: 800, whiteSpace: "nowrap", flex: "none" }}>Download</span>
        </a>
      </div>

      {/* Reliability score card */}
      {/* Appearance — light/dark theme toggle (Hauler-only, CSS-variable-based) */}
      {colorScheme && (
        <div style={cardStyle}>
          <div style={sectionLabel}>Appearance</div>
          <ThemeToggleRow
            isLight={colorScheme.isLight}
            onToggle={colorScheme.toggle}
          />
        </div>
      )}

      <SignOutButton />

      <div style={cardStyle}>
        <div style={sectionLabel}>Reliability score</div>
        {!session && (
          <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 8 }}>
            Sign in to see your score.
          </div>
        )}
        {session && score && (
          <>
            <div style={{
              fontSize: 56,
              fontWeight: 800,
              color: COLORS.text,
              letterSpacing: -2,
              lineHeight: 1,
              marginTop: 6,
            }}>
              {score.currentScore}
            </div>
            <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
              Score range: 0–100
            </div>
          </>
        )}
        {session && !score && !scoreErr && (
          <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 8 }}>Loading…</div>
        )}
        {scoreErr && (
          <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 8 }}>
            Score unavailable right now.
          </div>
        )}

        <div style={{
          marginTop: 14,
          padding: "12px 14px",
          background: "rgba(61,144,217,.08)",
          border: `1px solid rgba(61,144,217,.3)`,
          borderRadius: 10,
          fontSize: 12,
          color: COLORS.text,
          lineHeight: 1.5,
        }}>
          Your reliability score is informational. It does not affect which jobs you can see or claim. All eligible jobs appear in your list regardless of score.
        </div>
      </div>
    </div>
  );
}

function ThemeToggleRow({ isLight, onToggle }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 0 4px",
      gap: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>{isLight ? "☀️" : "🌙"}</span>
        <div>
          <div style={{ fontSize: 13, color: COLORS.text, fontWeight: 600 }}>
            {isLight ? "Light" : "Dark"}
          </div>
          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>
            Tap to switch theme
          </div>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={isLight}
        aria-label="Toggle light or dark theme"
        onClick={onToggle}
        style={{
          width: 46,
          height: 26,
          minWidth: 46,
          minHeight: 44,
          borderRadius: 999,
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{
          display: "inline-block",
          width: 46,
          height: 26,
          borderRadius: 999,
          background: isLight ? COLORS.sky : "var(--hl-border)",
          position: "relative",
          transition: "background .15s ease",
        }}>
          <span style={{
            position: "absolute",
            top: 3,
            left: isLight ? 23 : 3,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#FFFFFF",
            boxShadow: "0 1px 3px rgba(0,0,0,.25)",
            transition: "left .15s ease",
          }} />
        </span>
      </button>
    </div>
  );
}

function SignOutButton() {
  const [busy, setBusy] = useState(false);
  async function handleSignOut() {
    if (busy) return;
    setBusy(true);
    try {
      await signOut();
      // useAuth listener picks up SIGNED_OUT and HaulerApp swaps to
      // SharedLoginScreen automatically. No reload needed.
    } catch (e) {
      console.warn("[ProfileScreen] signOut failed:", e?.message);
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={handleSignOut}
      disabled={busy}
      style={{
        width: "100%",
        padding: "12px",
        borderRadius: 12,
        border: `1px solid ${COLORS.red}`,
        background: "transparent",
        color: COLORS.red,
        fontFamily: "inherit",
        fontSize: 14,
        fontWeight: 700,
        cursor: busy ? "wait" : "pointer",
        marginBottom: 14,
        minHeight: 44,
      }}
    >
      {busy ? "Signing out…" : "Sign out"}
    </button>
  );
}

function Row({ label, value, last }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "10px 0",
      borderBottom: last ? "none" : `1px solid ${COLORS.border}`,
    }}>
      <span style={{ fontSize: 12, color: COLORS.sub }}>{label}</span>
      <div style={{ fontSize: 13, color: COLORS.text, fontWeight: 600, textAlign: "right" }}>
        {value}
      </div>
    </div>
  );
}

function ToggleRow({ label, sublabel, checked, loading, onToggle }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 0",
      borderBottom: `1px solid ${COLORS.border}`,
      gap: 12,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: COLORS.sub }}>{label}</div>
        {sublabel && (
          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>{sublabel}</div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={loading}
        onClick={onToggle}
        style={{
          width: 46,
          height: 26,
          minWidth: 46,
          minHeight: 44,
          borderRadius: 999,
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: loading ? "wait" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{
          display: "inline-block",
          width: 46,
          height: 26,
          borderRadius: 999,
          background: checked ? COLORS.green : "var(--hl-border)",
          opacity: loading ? 0.6 : 1,
          position: "relative",
          transition: "background .15s ease",
        }}>
          <span style={{
            position: "absolute",
            top: 3,
            left: checked ? 23 : 3,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#FFFFFF",
            boxShadow: "0 1px 3px rgba(0,0,0,.25)",
            transition: "left .15s ease",
          }} />
        </span>
      </button>
    </div>
  );
}

function EditableRow({ label, value, valueColor, isOpen, onToggle, children }) {
  return (
    <div style={{ borderBottom: `1px solid ${COLORS.border}` }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          padding: "10px 0",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
          color: COLORS.text,
          fontFamily: "inherit",
          cursor: "pointer",
          textAlign: "left",
          minHeight: 44,
        }}
      >
        <span style={{ fontSize: 12, color: COLORS.sub }}>{label}</span>
        <div style={{
          fontSize: 13,
          color: valueColor || COLORS.text,
          fontWeight: 600,
          textAlign: "right",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          <div style={{ textAlign: "right" }}>{value}</div>
          <span style={{ fontSize: 18, color: COLORS.sub, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s ease" }}>›</span>
        </div>
      </button>
      {isOpen && (
        <div style={{
          padding: "12px",
          borderRadius: 10,
          background: "rgba(255,255,255,.03)",
          border: `1px solid ${COLORS.border}`,
          margin: "0 0 12px",
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

function primaryBtn(loading) {
  return {
    border: "none",
    borderRadius: 999,
    background: loading ? "rgba(61,144,217,.5)" : COLORS.sky,
    color: "#FFFFFF",
    fontWeight: 800,
    padding: "9px 16px",
    fontFamily: "inherit",
    fontSize: 13,
    cursor: loading ? "wait" : "pointer",
    minHeight: 40,
  };
}

const cardStyle = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: "14px 16px",
  marginBottom: 14,
};

const sectionLabel = {
  fontSize: 11,
  letterSpacing: 1.5,
  fontWeight: 700,
  color: COLORS.sub,
  textTransform: "uppercase",
};

const dividerStyle = {
  height: 1,
  background: COLORS.border,
  margin: "12px 0 0",
};
